import React, { useEffect, useState } from 'react';
import { string } from 'prop-types';
import config from '../../config/config';
import { configPortfolio } from '../../config/configPortfolio';
import {
  resolveServicePage,
  servicePageIds,
  serviceRouteName,
} from '../../config/configServicePages';
import { t } from '../../util/messages';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
  ServiceGraphic,
  ServiceIcon,
} from '../../components';
import { TopbarContainer } from '..';
import SectionPortfolio from '../LandingPage/SectionPortfolio/SectionPortfolio';
import css from './ServicePage.module.css';

/**
 * One service, one URL.
 *
 * Every service route renders this component; `serviceId` arrives as an
 * extraProp from routeConfiguration.js and selects the content.
 * `resolveServicePage` returns it whether the service also has a card in the
 * landing grid (where configServices owns the title, description and tags, so
 * the two cannot drift apart) or is a standalone page that carries its own.
 *
 * SECTION ORDER, AND WHY
 * ----------------------
 * A visitor arriving from a search for "mobile app development company" is
 * deciding whether to make contact, and the sections answer the questions
 * they ask in roughly the order they ask them: what is this, what do I get,
 * what problem does it fix, how does it work, what does it include, what else
 * do I want to know, who else could help. Earlier this page was a heading and
 * two paragraphs, which answered the first question and none of the rest.
 *
 * The FAQ block does double duty: those questions are close to how people
 * actually search, and the answers are the part of the page most likely to be
 * read in full.
 *
 * WHAT IS DELIBERATELY ABSENT
 * ---------------------------
 * No statistics bar, no client logo wall, no awards row. The comparable pages
 * this was modelled on lead with "18+ years, 400+ people, 2500+ projects" and
 * press logos; every number of that kind here would have to be invented, and
 * an invented number is worse than an absent one on a page whose whole job is
 * to be credible. `caseStudyIds` and `reviewId` are config slots that render
 * nothing until real ones are chosen — see configServicePages.js.
 */
/** How many projects each service page shows. */
const PROJECTS_SHOWN = 3;

/**
 * Three projects for the first render — chosen from the service id, so the
 * prerendered HTML, the hydration pass and every crawler see the same three.
 * Math.random() here would make server and client markup differ and break
 * hydration.
 */
const stablePick = (pool, seed) => {
  const hash = Array.from(seed).reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  const start = hash % pool.length;
  return Array.from({ length: Math.min(PROJECTS_SHOWN, pool.length) }, (_, i) => {
    return pool[(start + i) % pool.length];
  });
};

/** Fisher-Yates, so the visit-to-visit shuffle is not biased. */
const randomPick = pool => {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, PROJECTS_SHOWN);
};

const ServicePage = ({ serviceId }) => {
  const [scrollingDisabled, setScrollingDisabled] = useState(false);

  /* SectionPortfolio's cards carry data-reveal; this is what arms them. */
  useScrollReveal();

  const onManageDisableScrolling = (_id, disabled = false) => {
    setScrollingDisabled(disabled);
  };

  const { siteTitle, canonicalRootURL, configProcess, configReviews } = config;

  const page = resolveServicePage(serviceId);

  /*
   * A route only exists for an id in the registry, so this cannot happen
   * through navigation — but a hand-typed `serviceId` would render a blank
   * page, and the prerenderer treats an empty render as a build failure.
   * Fail loudly instead of silently shipping nothing.
   */
  if (!page) {
    throw new Error(`ServicePage: no service content for id "${serviceId}".`);
  }

  const title = `${page.metaTitle} | ${siteTitle}`;
  const url = `${canonicalRootURL}/services/${page.slug}`;
  const others = servicePageIds.filter(id => id !== serviceId).map(resolveServicePage);

  /* Both resolve to [] / undefined until a real mapping is chosen. */
  const caseStudies = (page.caseStudyIds || [])
    .map(id => configPortfolio.find(project => project.id === id))
    .filter(Boolean);
  /*
   * Every service page shows the same kind of proof: three projects from the
   * shared portfolio. A page may narrow the pool with `caseStudyIds`; the rest
   * draw from all of it. The first paint is deterministic (see stablePick);
   * once mounted the three are reshuffled, so a returning visitor sees a
   * different mix. Cards are keyed by position in SectionPortfolio, so their
   * DOM nodes — and the scroll-reveal state on them — survive the swap.
   */
  const pool = caseStudies.length > 0 ? caseStudies : configPortfolio;
  const [projects, setProjects] = useState(() => stablePick(pool, serviceId));
  useEffect(() => {
    setProjects(randomPick(pool));
  }, [serviceId]);
  const review = page.reviewId
    ? configReviews.find(entry => entry.id === page.reviewId)
    : undefined;

  return (
    <Page
      title={title}
      description={page.metaDescription}
      scrollingDisabled={scrollingDisabled}
      schema={[
        {
          '@context': 'https://schema.org',
          '@type': 'Service',
          '@id': `${url}#service`,
          name: page.metaTitle,
          serviceType: page.title,
          description: page.metaDescription,
          url,
          /* Resolves to the single Organization node Page always emits. */
          provider: { '@id': `${canonicalRootURL}/#organization` },
          ...(page.tags?.length
            ? {
                hasOfferCatalog: {
                  '@type': 'OfferCatalog',
                  name: `${page.title} capabilities`,
                  itemListElement: page.tags.map(tag => ({
                    '@type': 'Offer',
                    itemOffered: { '@type': 'Service', name: tag },
                  })),
                },
              }
            : {}),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': url,
          name: title,
          description: page.metaDescription,
          url,
          isPartOf: { '@id': `${canonicalRootURL}/#website` },
          /*
           * Three levels now that /services exists. It was two while there
           * was no hub, because naming a parent that 404s is worse than
           * having none.
           */
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: siteTitle, item: canonicalRootURL },
              {
                '@type': 'ListItem',
                position: 2,
                name: t('ServicePage.breadcrumbServices'),
                item: `${canonicalRootURL}/services`,
              },
              { '@type': 'ListItem', position: 3, name: page.metaTitle, item: url },
            ],
          },
        },
        /*
         * FAQPage for the question block. Google has restricted FAQ rich
         * results to authoritative health and government sites, so this is
         * unlikely to change how the page looks in results; it is valid
         * structured data that describes the content accurately, which is
         * worth having for the engines and assistants that do read it.
         */
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          '@id': `${url}#faq`,
          mainEntity: page.faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: { '@type': 'Answer', text: faq.answer },
          })),
        },
      ]}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer onManageDisableScrolling={onManageDisableScrolling} />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain>
          <article className={css.root}>
            {/* ---- Hero ---------------------------------------------- */}
            <header className={css.header}>
              <div className={css.headerCopy}>
                <nav className={css.breadcrumb} aria-label="Breadcrumb">
                  <NamedLink name="LandingPage">{t('ServicePage.breadcrumbHome')}</NamedLink>
                  <span aria-hidden="true">/</span>
                  <NamedLink name="ServicesPage">{t('ServicePage.breadcrumbServices')}</NamedLink>
                  <span aria-hidden="true">/</span>
                  <span>{page.title}</span>
                </nav>

                <ServiceIcon serviceId={page.artId} className={css.mark} />
                {page.badge && (
                  <p className={css.badge}>
                    {page.badge.logo && (
                      <img
                        src={page.badge.logo}
                        alt={page.badge.logoAlt || ''}
                        width="20"
                        height="20"
                      />
                    )}
                    <span>{page.badge.label}</span>
                  </p>
                )}
                <h1 className={css.heading}>{page.heading}</h1>
                <p className={css.intro}>{page.intro}</p>

                <div className={css.heroActions}>
                  <NamedLink className={css.cta} name="LandingPage" to={{ hash: '#contact-us' }}>
                    {t('ServicePage.ctaAction')}
                  </NamedLink>
                  <NamedLink
                    className={css.ctaSecondary}
                    name="LandingPage"
                    to={{ hash: '#portfolio' }}
                  >
                    {t('ServicePage.ctaSecondary')}
                  </NamedLink>
                </div>
              </div>

              {/* Drawn, not photographed — see ServiceGraphic for why. */}
              <div className={css.headerArt}>
                <ServiceGraphic serviceId={page.graphicId || page.artId} className={css.graphic} />
              </div>
            </header>

            {/* ---- What you get -------------------------------------- */}
            <section className={css.section} aria-labelledby="service-value">
              <h2 id="service-value" className={css.sectionHeading}>
                {t('ServicePage.valueHeading')}
              </h2>
              <ul className={css.valueGrid}>
                {page.valueProps.map(prop => (
                  <li key={prop.title} className={css.valueCard}>
                    <span className={css.valueRule} aria-hidden="true" />
                    <h3 className={css.valueTitle}>{prop.title}</h3>
                    <p className={css.valueBody}>{prop.body}</p>
                  </li>
                ))}
              </ul>
            </section>

            {/* ---- Extra card sections: only pages that define them ---- */}
            {(page.extraSections || []).map(section => (
              <section
                key={section.id}
                className={css.section}
                aria-labelledby={`service-${section.id}`}
              >
                <h2 id={`service-${section.id}`} className={css.sectionHeading}>
                  {section.heading}
                </h2>
                {section.intro && <p className={css.sectionIntro}>{section.intro}</p>}
                <ul className={css.valueGrid}>
                  {section.items.map(item => (
                    <li key={item.title} className={css.valueCard}>
                      <span className={css.valueRule} aria-hidden="true" />
                      <h3 className={css.valueTitle}>{item.title}</h3>
                      <p className={css.valueBody}>{item.body}</p>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            {/* ---- Overview prose ------------------------------------ */}
            <section className={css.section} aria-labelledby="service-overview">
              <h2 id="service-overview" className={css.sectionHeading}>
                {t('ServicePage.overviewHeading')}
              </h2>
              <div className={css.body}>
                {page.body.map(paragraph => (
                  <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                ))}
                {page.bodyLink && (
                  <p>
                    {page.bodyLink.lead}
                    <NamedLink name={serviceRouteName(page.bodyLink.id)}>
                      {page.bodyLink.text}
                    </NamedLink>
                    {page.bodyLink.trail}
                  </p>
                )}
              </div>
            </section>

            {/* ---- Problems this solves ------------------------------ */}
            <section className={css.section} aria-labelledby="service-challenges">
              <h2 id="service-challenges" className={css.sectionHeading}>
                {t('ServicePage.challengesHeading')}
              </h2>
              <ol className={css.challengeList}>
                {page.challenges.map(challenge => (
                  <li key={challenge.title} className={css.challenge}>
                    <h3 className={css.challengeTitle}>{challenge.title}</h3>
                    <p className={css.challengeBody}>{challenge.body}</p>
                  </li>
                ))}
              </ol>
            </section>

            {/* ---- How we work: the site-wide process, not a per-page one */}
            <section className={css.section} aria-labelledby="service-process">
              <h2 id="service-process" className={css.sectionHeading}>
                {t('ServicePage.processHeading')}
              </h2>
              <ol className={css.processList}>
                {configProcess.map((step, index) => (
                  <li key={step.id} className={css.processStep}>
                    <span className={css.processHead}>
                      {/* The same four marks the landing page's process
                          section uses, so the two read as one site. */}
                      <img
                        className={css.processIcon}
                        src={step.icon}
                        alt=""
                        width="28"
                        height="31"
                        loading="lazy"
                      />
                      <span className={css.processNumber} aria-hidden="true">
                        {index + 1}
                      </span>
                    </span>
                    <h3 className={css.processTitle}>{step.title}</h3>
                    <p className={css.processBody}>{step.description}</p>
                  </li>
                ))}
              </ol>
            </section>

            {/* ---- What this includes -------------------------------- */}
            {page.tags.length > 0 && (
              <section className={css.section} aria-labelledby="service-includes">
                <h2 id="service-includes" className={css.sectionHeading}>
                  {t('ServicePage.includesHeading')}
                </h2>
                <ul className={css.tags}>
                  {page.tags.map(tag => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              </section>
            )}

            {/*
              * The landing page's own portfolio section, as on the hub —
              * not a second, lesser version of it. It brings the featured
              * treatment, the per-project metrics and the category chips.
              *
              * Three projects on every service page, not the whole set.
              */}
            <SectionPortfolio className={css.portfolio} projects={projects} />

            {/* ---- Client quote: only where a real one is chosen ----- */}
            {review && (
              <figure className={css.quote}>
                {/* `excerpt`, not `review`: the full quotes run several
                    hundred words, which is a landing-page section, not a
                    supporting note on a service page. */}
                <blockquote className={css.quoteText}>{review.excerpt}</blockquote>
                <figcaption className={css.quoteSource}>
                  {review.clientName}
                  {review.clientPosition ? `, ${review.clientPosition}` : ''}
                </figcaption>
              </figure>
            )}

            {/* ---- Questions ---------------------------------------- */}
            <section className={css.section} aria-labelledby="service-faq">
              <h2 id="service-faq" className={css.sectionHeading}>
                {t('ServicePage.faqHeading')}
              </h2>
              {/*
                * Native <details>, not a JavaScript accordion.
                *
                * It opens and closes with no script, so it works in the
                * prerendered HTML before hydration and keeps working if the
                * bundle fails; it is a disclosure widget to a screen reader
                * without any ARIA to get wrong; and the answers stay in the
                * DOM while closed, so they are still indexed and still found
                * by in-page search.
                *
                * The first is open, because a panel where everything is shut
                * reads as a wall of questions and tells a visitor nothing
                * about the answers underneath.
                */}
              <div className={css.faqList}>
                {page.faqs.map((faq, index) => (
                  <details key={faq.question} className={css.faq} open={index === 0}>
                    <summary className={css.faqQuestion}>
                      <span>{faq.question}</span>
                      <svg
                        className={css.faqChevron}
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3.5 6l4.5 4.5L12.5 6"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </summary>
                    <p className={css.faqAnswer}>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </section>

            {/* ---- Other services ----------------------------------- */}
            <section className={css.related} aria-labelledby="service-related">
              <h2 id="service-related" className={css.sectionHeading}>
                {t('ServicePage.relatedHeading')}
              </h2>
              <ul className={css.relatedList}>
                {others.map(other => (
                  <li key={other.id}>
                    <NamedLink name={serviceRouteName(other.id)} className={css.relatedLink}>
                      <span className={css.relatedTitle}>{other.title}</span>
                      <span className={css.relatedDescription}>{other.description}</span>
                    </NamedLink>
                  </li>
                ))}
              </ul>
            </section>

            {/* ---- Closing call to action ---------------------------- */}
            <aside className={css.closing}>
              <h2 className={css.closingHeading}>{t('ServicePage.closingHeading')}</h2>
              <p className={css.closingBody}>{t('ServicePage.closingBody')}</p>
              <NamedLink className={css.cta} name="LandingPage" to={{ hash: '#contact-us' }}>
                {t('ServicePage.ctaAction')}
              </NamedLink>
            </aside>
          </article>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <div className={css.footerContainer}>
            <Footer />
          </div>
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

ServicePage.propTypes = {
  serviceId: string.isRequired,
};

export default ServicePage;
